<script lang="ts">
  import { FieldProps } from '../Field/Field.types';
  import Field from '../Field/Field.vue';
  import { RadioGroupOption, RadioGroupVariant, RadioGroupVariants } from './RadioGroup.types';

  export * from './RadioGroup.keys';
  export * from './RadioGroup.types';

  export interface RadioGroupProps extends FieldProps {
    /**
     * Passed to the "name" attribute of the `<input>` elements within the RadioGroup.
     */
    name?: string;

    /**
     * Deprecated - Compose your radio group with the `Radio` component instead
     * @deprecated
     */
    options?: RadioGroupOption[];

    /**
     * The value of the selected radio option. Use this prop with the `update:modelValue` event to create a controlled component.
     */
    modelValue?: RadioGroupOption['value'];

    /**
     * @defaultValue 'radio'
     */
    variant?: RadioGroupVariants;

    /**
     * Whether the group should expand to the parent's width
     */
    fullWidth?: boolean;

    /**
     * Default orientation. If the variant is `button` and the group is wider than its parent it will be displayed in a column, and this prop will be ignored.
     *
     * Note: This does not work with the deprecated `options` prop. Update those instances to use the `RadioNew` component and default slot.
     *
     * @defaultValue 'horizontal'
     */
    orientation?: 'horizontal' | 'vertical';
  }
</script>

<script setup lang="ts">
  import uniqueId from 'lodash-es/uniqueId';
  import { computed, onMounted, onUnmounted, provide, ref, useCssModule, useTemplateRef, watchEffect } from 'vue';

  import { isComponentWithEl, toComponentEl } from '../../utils/templateRefNarrowing';
  import VariantButton from './components/VariantButton.vue';
  import VariantChip from './components/VariantChip.vue';
  import VariantRadio from './components/VariantRadio.vue';
  import VariantTile from './components/VariantTile.vue';
  import { RADIO_GROUP_INJECTION } from './RadioGroup.keys';

  const variantComponentsMap = {
    [RadioGroupVariant.Button]: VariantButton,
    [RadioGroupVariant.Chip]: VariantChip,
    [RadioGroupVariant.Radio]: VariantRadio,
    [RadioGroupVariant.Tile]: VariantTile,
  };

  const props = withDefaults(defineProps<RadioGroupProps>(), {
    variant: 'radio',
    fullWidth: false,
    modelValue: undefined,
    name: undefined,
    options: undefined,
    orientation: 'horizontal',
  });

  const emit = defineEmits<{
    /**
     * Occurs when the user selects a radio option. Also, it enables v-model usage on the component.
     */
    (e: 'update:modelValue', value: RadioGroupOption['value']): void;
  }>();

  const classes = useCssModule();

  function update(e: Event) {
    emit('update:modelValue', (e.target as HTMLInputElement).value);
  }

  const errorId = uniqueId('radio-group-field-error-');

  provide(RADIO_GROUP_INJECTION.key, {
    name: computed(() => props.name),
    disabled: computed(() => props.isDisabled || props.disabled),
    fullWidth: computed(() => props.fullWidth),
    modelValue: computed(() => props.modelValue),
    options: computed(() => props.options),
    variant: computed(() => props.variant),
    orientation: computed(() => internalOrientation.value),
    update,
  });

  const internalOrientation = ref(props.orientation);

  const defaultSlotClasses = computed(() => {
    return {
      'flex-wrap': props.variant !== 'button',
      'gap-x-1.5 gap-y-3': props.variant === 'chip' && internalOrientation.value === 'horizontal',
      'gap-6':
        props.variant === 'radio' ||
        props.variant === 'tile' ||
        (props.variant === 'chip' && internalOrientation.value === 'vertical'),
      'flex-col': internalOrientation.value === 'vertical',
    };
  });

  const resizeObserver = ref<ResizeObserver | null>(null);
  const radioGroupRef = useTemplateRef('radioGroup');
  const radioGroupParent = computed(() => {
    const comp = radioGroupRef.value;
    return isComponentWithEl(comp) ? comp.$el.parentElement : null;
  });
  const radioGroupButtons = computed(() => {
    if (props.variant !== 'button') {
      return [];
    }

    const el = toComponentEl(radioGroupRef.value);
    return el ? Array.from(el.querySelectorAll<HTMLElement>('[data-variant-button]')) : [];
  });

  function getButtonsWidth() {
    return radioGroupButtons.value.reduce((acc, button) => {
      const buttonWidth = button.getBoundingClientRect().width;
      return acc + buttonWidth;
    }, 0);
  }

  const initialTotalButtonsWidth = ref(0);

  const handleResize = (entry: ResizeObserverEntry) => {
    internalOrientation.value =
      initialTotalButtonsWidth.value > entry.contentBoxSize[0].inlineSize ? 'vertical' : props.orientation;
  };

  onMounted(() => {
    // If the width of the buttons is greater than the parent element,
    // set orientation to vertical
    if (props.variant === 'button' && !props.fullWidth) {
      initialTotalButtonsWidth.value = getButtonsWidth();
      resizeObserver.value = new ResizeObserver((entries) => handleResize(entries[0]));

      const parent = radioGroupParent.value;
      if (parent) resizeObserver.value.observe(parent);
    }
  });

  onUnmounted(() => {
    if (props.variant === 'button' && resizeObserver.value) {
      resizeObserver.value?.disconnect();
    }
  });

  watchEffect(() => {
    internalOrientation.value = props.orientation;
  });
</script>

<template>
  <Field
    v-bind="props"
    ref="radioGroup"
    class="stash-radio-group"
    :class="classes['stash-radio-group']"
    data-test="stash-radio-group"
    :aria-errormessage="errorId"
    :error-id="errorId"
    :aria-invalid="!!props.errorText"
    role="radiogroup"
    fieldset
  >
    <slot v-if="props.options">
      <component :is="variantComponentsMap[props.variant]" />
    </slot>
    <div v-else class="flex" data-test="stash-radio-group|default-slot" :class="defaultSlotClasses">
      <slot></slot>
    </div>
  </Field>
</template>

<style module>
  @layer utilities {
    .stash-radio-group :global(.stash-field-hint),
    .stash-radio-group :global(.stash-field-error) {
      display: block;
      width: 100%;
    }

    .stash-radio-group > :global(.stash-label) {
      margin-bottom: 4px;
    }
  }
</style>
